import React, { useMemo } from "react";
import PriorityBadge from "@/components/LearningSession/PriorityBadge";
import { accuracyToColor } from "@/lib/theme";

const PROFICIENCY_METRICS = [
  { key: "intelligibility", label: "Intelligibility", hint: "How easily you're understood" },
  { key: "fluency", label: "Fluency", hint: "Smoothness, pace and pauses" },
  { key: "prosody", label: "Prosody", hint: "Rhythm, stress and melody" },
];

// Overall score ring for the session headline.
const HeroRing = ({ value }) => {
  const rounded = Math.round(Number.isFinite(value) ? value : 0);
  const color = accuracyToColor(rounded);
  return (
    <div
      className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full sm:h-24 sm:w-24"
      style={{
        background: `conic-gradient(${color} ${rounded * 3.6}deg, rgb(var(--line)) 0deg)`,
        boxShadow: `0 0 32px -8px ${color}`,
      }}
      aria-label={`Overall score ${rounded} out of 100`}
      title="Pronunciation accuracy across every sound you said"
    >
      <div className="flex h-[70px] w-[70px] flex-col items-center justify-center rounded-full bg-canvas sm:h-[84px] sm:w-[84px]">
        <span className="font-display text-2xl font-bold leading-none sm:text-3xl" style={{ color }}>
          {rounded}
        </span>
        <span className="mt-1 text-center text-[9px] uppercase leading-tight tracking-wider text-fg-subtle">
          Overall
          <br />
          score
        </span>
      </div>
    </div>
  );
};

const SessionResults = ({
  wordsPerMinute,
  accuracy,
  elapsedTime,
  repeatable,
  fullRepeatCount,
  proficiency,
  words = [],
}) => {
  const formatDuration = (seconds) => {
    if (seconds === undefined || Number.isNaN(seconds)) {
      return "—";
    }
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) {
      return `${mins}m${secs > 0 ? ` ${secs}s` : ""}`.trim();
    }
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h${remainingMins > 0 ? ` ${remainingMins}m` : ""}`.trim();
  };

  const getPerformanceSummary = (score) => {
    if (score >= 96) {
      return {
        title: "Exceptional articulation",
        subtitle: "Ultra-clean delivery. Layer in expression to stretch further.",
      };
    }
    if (score >= 88) {
      return {
        title: "Strong pronunciation",
        subtitle: "Only small slips detected. A focused pass will lock it in.",
      };
    }
    if (score >= 75) {
      return {
        title: "Steady progress",
        subtitle: "You're close—slow the tricky syllables to climb higher.",
      };
    }
    return {
      title: "Room to grow",
      subtitle: "Break the highlighted words into smaller chunks and rebuild.",
    };
  };

  const categorizePace = (wpm) => {
    if (wpm >= 130) {
      return "High tempo—keep clarity sharp while sustaining that flow.";
    }
    if (wpm >= 90) {
      return "Comfortable pace—balanced between clarity and momentum.";
    }
    if (wpm >= 60) {
      return "Deliberate tempo—add a little speed once accuracy feels locked.";
    }
    return "Warm-up pace—try another pass slightly quicker for confidence.";
  };

  const repeatableSummary = useMemo(() => {
    if (!repeatable) {
      return null;
    }

    if (fullRepeatCount >= 4) {
      return "Impressive stamina—those loops are cementing muscle memory.";
    }
    if (fullRepeatCount >= 2) {
      return "Nice rhythm—another loop or two will deepen the pattern.";
    }
    return "Finish a couple more cycles to maximise the repeatable drill.";
  }, [repeatable, fullRepeatCount]);

  const proficiencyScores = PROFICIENCY_METRICS.map((metric) => ({
    ...metric,
    value: proficiency?.[metric.key]?.value,
    note: proficiency?.[metric.key]?.note,
  })).filter((metric) => typeof metric.value === "number");
  const hasProficiency = proficiencyScores.length > 0;
  // The headline is pronunciation accuracy across every sound in the session.
  const headlineScore = accuracy;

  const performanceSummary = useMemo(
    () => getPerformanceSummary(headlineScore),
    [headlineScore]
  );

  // Functional load: mispronunciations that could turn a word into another word.
  const meaningRisks = words
    .filter((word) => word?.meaning_risk)
    .sort((a, b) => (b.meaning_risk.score || 0) - (a.meaning_risk.score || 0))
    .slice(0, 4)
    .map((word) => {
      const index = (word.sound_risks || []).findIndex(
        (risk) => risk && risk.score === word.meaning_risk.score
      );
      const [target] = Object.keys(word.phoneme_matches?.[index] || {});
      const [said] = Object.keys(word.transcribed_phonemes?.[index] || {});
      return {
        word: (word.text || "").replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""),
        target,
        said,
        ...word.meaning_risk,
      };
    });

  const metrics = repeatable
    ? [
        {
          label: "Loops completed",
          value: `${fullRepeatCount}`,
        },
        {
          label: "Loop rate",
          value: `${Math.max(
            0,
            Math.floor(wordsPerMinute * fullRepeatCount)
          )}`,
        },
      ]
    : [
        {
          label: "Words per minute",
          value: `${wordsPerMinute.toFixed(0)}`,
        },
        {
          label: "Time elapsed",
          value: formatDuration(elapsedTime),
        },
      ];

  // The weakest proficiency area is the one to practise next.
  const focusKey =
    proficiencyScores.length > 1
      ? proficiencyScores.reduce((low, metric) => (metric.value < low.value ? metric : low)).key
      : null;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-line bg-canvas/60 p-4 text-fg shadow-panel backdrop-blur sm:p-6 md:p-10">
      {/* Headline: overall score, verdict, pace */}
      <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-4 sm:gap-5">
          <HeroRing value={headlineScore} />
          <div className="min-w-0">
            <p className="hud-label">
              {repeatable ? "Repeat session" : "Session summary"}
            </p>
            <h3 className="mt-1 font-display text-xl font-bold leading-tight text-fg sm:text-2xl md:text-3xl">
              {repeatable
                ? `${fullRepeatCount} full cycle${fullRepeatCount === 1 ? "" : "s"}`
                : performanceSummary.title}
            </h3>
            <p className="mt-1 max-w-md text-sm leading-relaxed text-fg-muted">
              {repeatable
                ? `${repeatableSummary ?? ""}${repeatableSummary ? " " : ""}${performanceSummary.subtitle}`
                : performanceSummary.subtitle}
            </p>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-3 sm:flex sm:flex-wrap">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-lg border border-line bg-surface px-4 py-3 sm:min-w-[112px]"
            >
              <div className="font-display text-2xl font-bold leading-none text-fg">
                {metric.value}
              </div>
              <div className="mt-1.5 text-xs text-fg-muted">{metric.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Speaking profile */}
      {hasProficiency ? (
        <div className="mt-6 grid gap-3 sm:mt-8 sm:gap-4 lg:grid-cols-3">
          {proficiencyScores.map((metric) => {
            const color = accuracyToColor(metric.value);
            const isFocus = metric.key === focusKey;
            return (
              <div
                key={metric.key}
                className={`flex flex-col rounded-xl border bg-surface p-4 sm:p-5 ${
                  isFocus ? "border-accent/50" : "border-line"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-base font-semibold text-fg">{metric.label}</div>
                  {isFocus && (
                    <span className="shrink-0 rounded-sm border border-accent/50 bg-accent/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-accent">
                      Focus here
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-fg-subtle">{metric.hint}</div>

                <div className="mt-3 flex items-baseline gap-1 sm:mt-4">
                  <span className="font-display text-3xl sm:text-4xl font-bold leading-none" style={{ color }}>
                    {Math.round(metric.value)}
                  </span>
                  <span className="text-sm text-fg-subtle">/100</span>
                </div>
                <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-line">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(0, Math.min(100, metric.value))}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>

                {metric.note && (
                  <p className="mt-3 text-sm leading-6 text-fg-muted sm:mt-4">{metric.note}</p>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {meaningRisks.length > 0 && (
        <div className="mt-8 border-t border-line pt-6">
          <div className="hud-label">Sounds that change meaning</div>
          <p className="mt-1 text-sm text-fg-muted">
            These slips could make a listener hear a different word. Fix them first.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {meaningRisks.map((risk, index) => (
              <div
                key={`${risk.word}-${index}`}
                className="rounded-lg border border-line bg-surface p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display text-lg font-semibold text-fg">{risk.word}</span>
                  <PriorityBadge priority={risk.priority} />
                </div>
                <div className="mt-2 flex items-center gap-2 font-mono text-base">
                  <span className="text-fg">/{risk.target}/</span>
                  <span className="text-fg-subtle">→</span>
                  <span className="text-accent">{risk.said ? `/${risk.said}/` : "missing"}</span>
                </div>
                {risk.sounds_like && (
                  <p className="mt-1 text-sm text-fg-muted">
                    Could sound like <span className="font-semibold text-fg">“{risk.sounds_like}”</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionResults;
